import { useState, useRef, useMemo } from "react";
import { useListTags } from "@workspace/api-client-react";
import { Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";

function slugify(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export interface TagSelectorProps {
  selected: string[]; // slugs
  onChange: (next: string[]) => void;
  /** Compact chip palette + free-text add. Used in editor + search filter. */
  className?: string;
  placeholder?: string;
}

/**
 * Multi-select tag chip control. Pulls the known-tag vocabulary from
 * /api/tags and lets the user toggle existing tags or add a new one.
 * Newly typed tags are slugified on the client; the server will create
 * the row on first use via setRecipeTags / resolveTagIds.
 */
export function TagSelector({
  selected,
  onChange,
  className,
  placeholder = "Add a tag…",
}: TagSelectorProps) {
  const { data } = useListTags();
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const known: { slug: string; name: string; count: number }[] = data?.tags ?? [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Selected tags first, then the rest of the known palette ordered by
  // popularity (count desc, already returned that way by the API).
  const orderedKnown = useMemo(() => {
    const sel: typeof known = [];
    const rest: typeof known = [];
    for (const t of known) {
      (selectedSet.has(t.slug) ? sel : rest).push(t);
    }
    return [...sel, ...rest];
  }, [known, selectedSet]);

  function toggle(slug: string) {
    if (selectedSet.has(slug)) {
      onChange(selected.filter((s) => s !== slug));
    } else {
      onChange([...selected, slug]);
    }
  }

  function commitDraft() {
    const slug = slugify(draft);
    if (!slug) return;
    if (!selectedSet.has(slug)) onChange([...selected, slug]);
    setDraft("");
  }

  function nameFor(slug: string): string {
    return known.find((t) => t.slug === slug)?.name ??
      slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  }

  return (
    <div className={cn("space-y-2", className)}>
      {/* Currently selected (shown inline as removable chips) */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((slug) => (
            <button
              key={slug}
              type="button"
              onClick={() => toggle(slug)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {nameFor(slug)}
              <X className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}

      {/* Free-text add */}
      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitDraft();
            }
          }}
          placeholder={placeholder}
          className="flex-1 h-7 rounded-lg border border-border/60 bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40"
        />
        <button
          type="button"
          onClick={commitDraft}
          disabled={!slugify(draft)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-muted text-muted-foreground hover:text-primary hover:bg-primary/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-3 h-3" />
          Add
        </button>
      </div>

      {/* Known-tag palette (omit selected ones — they're already at the top) */}
      {orderedKnown.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {orderedKnown
            .filter((t) => !selectedSet.has(t.slug))
            .slice(0, 40)
            .map((t) => (
              <button
                key={t.slug}
                type="button"
                onClick={() => toggle(t.slug)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors border border-transparent hover:border-primary/20"
                title={t.count > 0 ? `${t.count} recipe${t.count !== 1 ? "s" : ""}` : "Unused"}
              >
                {t.name}
                {t.count > 0 && (
                  <span className="text-[10px] opacity-60">{t.count}</span>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

